import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import type { TeamRecord } from '@/domain/teamTypes';
import { pb } from '@/lib/pocketbase';
import { useSimulatorState } from '@/store/useSimulatorState';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export function ShareWithTeamsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const simulationId = useSimulatorState((s) => s.simulationId);
  const [teams, setTeams] = useState<TeamRecord[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open || !simulationId) return;
    (async () => {
      try {
        const [teamRecords, sim] = await Promise.all([
          pb.collection('teams').getFullList<TeamRecord>({ sort: 'name' }),
          pb.collection('simulations').getOne<{ teams: string[] }>(simulationId),
        ]);
        setTeams(teamRecords);
        setSelected(new Set(sim.teams));
      } catch {
        toast.error('Impossible de charger les équipes.');
      }
    })();
  }, [open, simulationId]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (!simulationId) return;
    setIsSaving(true);
    try {
      await pb.collection('simulations').update(simulationId, { teams: Array.from(selected) });
      toast.success('Partage mis à jour.');
      onOpenChange(false);
    } catch {
      toast.error('Impossible de mettre à jour le partage.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Partager avec une équipe</DialogTitle>
          <DialogDescription>
            Les membres des équipes sélectionnées pourront consulter et modifier cette simulation.
          </DialogDescription>
        </DialogHeader>

        {teams.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Tu n’as pas encore d’équipe — crée-en une depuis la page « Équipes ».
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {teams.map((team) => (
              <li key={team.id} className="flex items-center gap-2">
                <Checkbox
                  id={`team-${team.id}`}
                  checked={selected.has(team.id)}
                  onCheckedChange={() => toggle(team.id)}
                />
                <label htmlFor={`team-${team.id}`} className="text-sm">
                  {team.name}
                </label>
              </li>
            ))}
          </ul>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
